// Chat Vuex module (unified chat API)
import { chatApi } from '@/services/api'

export default {
  namespaced: true,
  state: {
    loading: false,
    error: null,
    // Temporary in-memory messages store keyed by `${target}:${id}`
    messages: {}
  },
  getters: {
    getMessages: (state) => (target, id) => state.messages[`${target}:${id}`] || []
  },
  mutations: {
    SET_LOADING(state, v) { state.loading = v },
    SET_ERROR(state, e) { state.error = e },
    ADD_MESSAGE(state, { target, id, message }) {
      const key = `${target}:${id}`
      if (!Array.isArray(state.messages[key])) state.messages[key] = []
      state.messages[key].push(message)
    },
    SET_MESSAGES(state, { target, id, messages }) {
      const key = `${target}:${id}`
      state.messages[key] = messages || []
    },
    START_THINKING(state, { target, id }) {
      const key = `${target}:${id}`
      if (!Array.isArray(state.messages[key])) state.messages[key] = []
      state.messages[key].push({ role: 'agent', content: '...', thinking: true, timestamp: new Date() })
    },
    RESOLVE_THINKING(state, { target, id, content, suggested_tasks }) {
      const key = `${target}:${id}`
      const list = state.messages[key] || []
      for (let i = list.length - 1; i >= 0; i--) {
        const msg = list[i]
        if (msg && msg.thinking) {
          list[i] = { ...msg, content, thinking: false, ...(suggested_tasks && { suggested_tasks }) }
          break
        }
      }
      state.messages[key] = list
    }
  },
  actions: {
    // Generic unified chat (kept for other targets)
    async send({ commit }, { target, id, message, agent_id, session_id, stream = false, context = {} }) {
      try {
        commit('SET_LOADING', true); commit('SET_ERROR', null)
        return await chatApi.chat(target, id, { message, agent_id, session_id, stream, context })
      } catch (e) {
        commit('SET_ERROR', e.message || 'Chat failed'); throw e
      } finally { commit('SET_LOADING', false) }
    },
    // Workstream-specific chat using unified API
    async sendWorkstream({ commit }, { id, message, chatMode = 'model', modelId = null, agentId = null, sessionId = null, context = {} }) {
      const target = 'workstream'
      // 1) Add user message
      commit('ADD_MESSAGE', { target, id, message: { role: 'user', content: message, timestamp: new Date() } })
      // 2) Show thinking indicator
      commit('START_THINKING', { target, id })
      try {
        commit('SET_LOADING', true); commit('SET_ERROR', null)

        // Use unified chat API with proper field handling
        const chatRequest = {
          message,
          chat_mode: chatMode,
          session_id: sessionId,
          context: context
        }

        // Set model_id for model mode, agent_id for agent mode
        if (chatMode === 'model' && modelId) {
          chatRequest.model_id = modelId
        } else if (chatMode === 'agent' && agentId) {
          chatRequest.agent_id = agentId
        }

        // Call unified chat API
        const res = await chatApi.chat(target, id, chatRequest)
        const reply = res?.message || res?.reply || res?.response || 'OK'

        commit('RESOLVE_THINKING', { target, id, content: String(reply) })
        return res
      } catch (e) {
        commit('RESOLVE_THINKING', { target, id, content: 'Error: ' + (e?.response?.data?.error || e.message) })
        commit('SET_ERROR', e.message || 'Chat failed')
        throw e
      } finally { commit('SET_LOADING', false) }
    },

    // Goal-specific chat using unified API
    async sendGoal({ commit }, { id, message, chatMode = 'model', modelId = null, sessionId = null, context = {} }) {
      const target = 'goal'
      commit('ADD_MESSAGE', { target, id, message: { role: 'user', content: message, timestamp: new Date() } })
      commit('START_THINKING', { target, id })
      try {
        commit('SET_LOADING', true); commit('SET_ERROR', null)

        const chatRequest = {
          message,
          chat_mode: chatMode,
          session_id: sessionId,
          context: context
        }

        // Set model_id for model mode
        if (chatMode === 'model' && modelId) {
          chatRequest.model_id = modelId
        }

        const res = await chatApi.chat(target, id, chatRequest)
        const reply = res?.message || 'OK'

        // For goal chat, include suggested tasks in the message
        const messageData = {
          role: 'assistant',
          content: String(reply)
        }

        if (res?.suggested_tasks && Array.isArray(res.suggested_tasks)) {
          messageData.suggested_tasks = res.suggested_tasks
        }

        commit('RESOLVE_THINKING', { target, id, content: messageData.content, suggested_tasks: messageData.suggested_tasks })
        return res
      } catch (e) {
        commit('RESOLVE_THINKING', { target, id, content: 'Error: ' + (e?.response?.data?.error || e.message) })
        commit('SET_ERROR', e.message || 'Chat failed')
        throw e
      } finally { commit('SET_LOADING', false) }
    },

    // Task-specific chat using unified API
    async sendTask({ commit }, { id, message, chatMode = 'model', modelId = null, agentId = null, sessionId = null, context = {} }) {
      const target = 'task'
      commit('ADD_MESSAGE', { target, id, message: { role: 'user', content: message, timestamp: new Date() } })
      commit('START_THINKING', { target, id })
      try {
        commit('SET_LOADING', true); commit('SET_ERROR', null)

        const chatRequest = {
          message,
          chat_mode: chatMode,
          session_id: sessionId,
          context: context
        }

        // Set model_id for model mode, agent_id for agent mode
        if (chatMode === 'model' && modelId) {
          chatRequest.model_id = modelId
        } else if (chatMode === 'agent' && agentId) {
          chatRequest.agent_id = agentId
        }

        const res = await chatApi.chat(target, id, chatRequest)
        const reply = res?.message || 'OK'

        commit('RESOLVE_THINKING', { target, id, content: String(reply) })
        return res
      } catch (e) {
        commit('RESOLVE_THINKING', { target, id, content: 'Error: ' + (e?.response?.data?.error || e.message) })
        commit('SET_ERROR', e.message || 'Chat failed')
        throw e
      } finally { commit('SET_LOADING', false) }
    },

    // Generic unified chat method (handles all target types)
    async sendUnified({ commit }, { target, id, message, chatMode = 'model', modelId = null, agentId = null, sessionId = null }) {
      commit('ADD_MESSAGE', { target, id, message: { role: 'user', content: message, timestamp: new Date() } })
      commit('START_THINKING', { target, id })
      try {
        commit('SET_LOADING', true); commit('SET_ERROR', null)

        const chatRequest = {
          message,
          chat_mode: chatMode,
          session_id: sessionId
        }

        // Set correct field based on chat mode
        if (chatMode === 'model' && modelId) {
          chatRequest.model_id = modelId
        } else if (chatMode === 'agent' && agentId) {
          chatRequest.agent_id = agentId
        }

        const res = await chatApi.chat(target, id, chatRequest)
        const reply = res?.message || 'OK'

        commit('RESOLVE_THINKING', { target, id, content: String(reply) })
        return res
      } catch (e) {
        commit('RESOLVE_THINKING', { target, id, content: 'Error: ' + (e?.response?.data?.error || e.message) })
        commit('SET_ERROR', e.message || 'Chat failed')
        throw e
      } finally { commit('SET_LOADING', false) }
    },
    async getSessions({ commit }, { target, id, params = {} }) {
      try { commit('SET_LOADING', true); commit('SET_ERROR', null); return await chatApi.getSessions(target, id, params) }
      finally { commit('SET_LOADING', false) }
    }
  }
}
